import { Component, EventEmitter, Input, Output, inject } from "@angular/core";
import { ThemeService } from "../../services/theme.service";
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { AuthService } from "../../services/auth.service";
import { AsyncPipe, NgClass } from "@angular/common";
import { RouterModule } from "@angular/router";
import { NavigationOption } from "common";

@Component({
    selector: 'lib-sidebar-content',
    standalone: true,
    imports: [NgClass, AsyncPipe, MatListModule, MatButtonModule, MatIconModule, MatDividerModule, RouterModule],
    template: `
    <div class="sidebar-content">
        <section class="px-3 pt-3">
            <h2>{{title}}
                <!-- Close button -->
                <button mat-icon-button (click)="closeDrawer.emit()" class="float-right t-1-rem" title="Close Menu">
                    <mat-icon>close</mat-icon>
                </button>
            </h2>
            <mat-divider></mat-divider>
        </section>

        <!-- Render one mat-list with all navigation options within it. -->
        @if (authService.isAuthenticated$ | async) {
        <mat-list>
        @for(nav of navigationOptions; track nav.path) {

        @if (nav.isHeader) {
            <div mat-subheader class="subheader-with-badge">
                {{nav.title}}
                @if (nav.badgeCount && nav.badgeCount > 0) {
                    <span class="badge">{{nav.badgeCount}}</span>
                }
                @if (nav.hasNotification && (!nav.badgeCount || nav.badgeCount === 0)) {
                    <span class="notification-dot"></span>
                }
            </div>
            @for(child of nav.children; track child.path) {
            <mat-list-item [routerLink]="child.path" [queryParams]="child.queryParam" class="child-nav" [title]="child.title"
                    (click)="toggleDrawer.emit()" [attr.cdkFocusInitial]="child.isCurrentNav ? '' : null" [ngClass]="{'active': child.isCurrentNav}">
                <mat-icon matListItemIcon>{{child.icon}}</mat-icon>
                <div matListItemTitle>{{child.title}}</div>
            </mat-list-item>
            }
        } @else {
        <mat-list-item [routerLink]="nav.path" [queryParams]="nav.queryParam" [title]="nav.title"
                (click)="toggleDrawer.emit()" [attr.cdkFocusInitial]="nav.isCurrentNav ? '' : null" [ngClass]="{'active': nav.isCurrentNav}">
            <mat-icon matListItemIcon class="large-icon">{{nav.icon}}</mat-icon>
            <div matListItemTitle>{{nav.title}}</div>
            <div matListItemLine>{{nav.description}}</div>
        </mat-list-item>
        }
        }
        </mat-list>
        }
        
        <!-- Theme toggle at bottom -->
        <section class="fixed-bottom w-100">

            @if (authService.isAuthenticated$ | async) {
            <!-- Logout button -->
            <button mat-stroked-button (click)="authService.logout(); closeDrawer.emit()" class="w-50 as-centre" title="Sign out">Sign out<mat-icon iconPositionEnd>logout</mat-icon></button>
            } @else {
            <!-- Login button -->
            <button mat-stroked-button routerLink="/login" class="w-50 as-centre" title="Sign in">Sign in<mat-icon iconPositionEnd>login</mat-icon></button>
            }

            <mat-divider [vertical]="true"></mat-divider>

            <!-- Toggle theme button -->
            <button mat-icon-button class="w-50" (click)="themeService.toggleTheme()" title="Toggle Theme">
                <mat-icon>{{ themeService.themeIcon }}</mat-icon>
            </button>
        </section>
    </div>
    `,
    styles: `
        .sidebar-content {
            display: inherit;
            height: 100%;
            width: 100%;
        }

        .t-1-rem {
            top: -0.75rem;
        }

        .fixed-bottom {
            bottom: 0;
            position: absolute;
            padding-bottom: 1rem;
            padding-top: 1rem;
            display: flex;
            justify-content: space-evenly;
            background: white;
            width: 100%;
            border-top: 1px solid #e0e0e0;

            .as-centre {
                align-self: center;
            }
        }

        .mat-mdc-list-item-icon {
            color: rgba(0, 0, 0, 0.54);
        }

        .large-icon {
            width: 2rem !important;
            height: 2rem !important;
            font-size: 2rem !important;
            line-height: 2rem !important;
            margin-top: 1.25rem !important;
        }

        .subheader-with-badge {
            display: flex;
            align-items: center;
            justify-content: space-between;

            .badge {
                background-color: #f44336;
                color: white;
                border-radius: 0.5rem;
                padding: 2px 5px;
                font-size: 11px;
                min-width: 1rem;
                text-align: center;
                line-height: 1.2;
            }

            .notification-dot {
                width: 0.5rem;
                height: 0.5rem;
                background-color: #f44336;
                border-radius: 50%;
            }
        }

        mat-list {
            padding-bottom: 5rem;
            
            mat-list-item {
                flex-wrap: wrap;

                mat-icon {
                    margin-right: 1.5rem;
                    margin-left: 1.5rem;
                }

                &:hover {
                    cursor: pointer;
                    background-color: #f5f5f5;
                }
            }
        }

        ::ng-deep {

            .child-nav {
                height: 2.5rem !important;
                line-height: 2rem;

                mat-icon {
                    height: 1rem !important;
                    width: 1rem !important;
                    font-size: 1rem;
                    line-height: 1rem;
                }
            }

            .dark-theme {
                .fixed-bottom {
                    background: #424242;
                }

                mat-list mat-list-item:hover {
                    background-color: #898989 !important;
                }
            }
        }
    `
})
export class SidebarContentComponent {

    @Input()
    public title = '';

    @Input()
    public navigationOptions: NavigationOption[] = [];

    @Output()
    public toggleDrawer = new EventEmitter<void>();

    @Output()
    public closeDrawer = new EventEmitter<void>();

    public authService = inject(AuthService);
    public themeService = inject(ThemeService);

}